import { Avatar, Card, Colors, Text, getRelativeTime } from '@amelio/ui-library';

import type { QuestionComment } from './types';

import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import i18n from '@/plugins/i18n';
import { getInitials, getProfilePictureUrl } from '@/utils/user';

interface ReplyItemProps {
    reply: QuestionComment;
    dataTestId?: string;
}

export const ReplyItem = ({ reply, dataTestId = 'reply-item' }: ReplyItemProps) => {
    const { t } = useAmelioTranslations();

    const anonymousLabel = t('questionResult.anonymous');
    const replyDisplayName = reply.user
        ? `${reply.user.firstName} ${reply.user.lastName}`
        : anonymousLabel;
    const replyTimestamp = getRelativeTime({
        date: reply.timestamp,
        language: i18n.language,
    });

    return (
        <div
            key={reply.id}
            className={'grid w-full grid-cols-[auto_auto_1fr] items-start gap-3'}
            data-testid={dataTestId}
        >
            <div className={'shrink-0'} />

            <div className={'shrink-0'}>
                <Avatar
                    alt={replyDisplayName}
                    initials={reply.user ? getInitials(reply.user) : 'A'}
                    src={getProfilePictureUrl(reply.user?.profilePictureUrl || '')}
                    size={'xs'}
                />
            </div>

            <Card
                padding={'none'}
                className={
                    'border-base-100 flex min-w-0 flex-col overflow-hidden rounded-lg border-t border-b bg-white px-2 py-1.5'
                }
            >
                <div className={'min-w-0 items-center justify-between gap-2 sm:flex'}>
                    <Text
                        color={Colors.BASE}
                        weight={'semibold'}
                        className={'min-w-0 leading-tight'}
                    >
                        {replyDisplayName}
                    </Text>

                    <Text
                        color={Colors.BASE_400}
                        className={'shrink-0'}
                    >
                        {replyTimestamp}
                    </Text>
                </div>

                <Text
                    color={Colors.BASE}
                    className={'mt-2 leading-relaxed wrap-break-word'}
                >
                    {reply.text}
                </Text>
            </Card>
        </div>
    );
};
